/* ============================================================
   SINGSPACE ADMIN - SECTION INTÉGRATIONS CLOUD (v3 Soft)
   ============================================================ */

import { Button } from '@/components/ui/button';
import { apiService } from '@/services/api';
import { Cloud, Database, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { SectionTitle } from './SectionTitle';

export function CloudIntegrationSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<{ isConnected: boolean; email?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const fetchStatus = useCallback(async (showSuccessToast = false) => {
    try {
      const response = await apiService.getGoogleAuthStatus();
      // Le backend renvoie { success: true, connected: boolean, email: string }
      const connected = !!response.data.connected;

      setStatus({
        isConnected: connected,
        email: response.data.email,
      });

      if (showSuccessToast && connected) {
        toast.success('Google Drive connecté avec succès');
      }
    } catch (error) {
      console.error('Error fetching Google Auth status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();

    // Si on vient d'être redirigé avec succès
    if (searchParams.get('googleDrive') === 'connected') {
      fetchStatus(true);
      // Nettoyer l'URL
      searchParams.delete('googleDrive');
      setSearchParams(searchParams, { replace: true });
    }
  }, [fetchStatus, searchParams, setSearchParams]);

  const handleConnect = async () => {
    setIsActionLoading(true);
    try {
      const response = await apiService.getGoogleAuthUrl();
      // Le backend renvoie { success: true, url: string }
      const url = response.data.url;
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      toast.error('Échec de la connexion');
      setIsActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Déconnecter Google Drive ?')) return;
    setIsActionLoading(true);
    try {
      await apiService.disconnectGoogleAuth();
      toast.success('Service déconnecté');
      await fetchStatus();
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setIsActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-6 h-6 text-[#00758d]" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <SectionTitle
        label="Services de Stockage"
        description="Liez un compte Google Drive pour l'archivage automatique des factures."
      />

      <div className="border border-slate-200 rounded-xl p-8 bg-white">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div className="flex items-start gap-6">
            <div
              className={`w-16 h-16 rounded-lg flex items-center justify-center ${
                status?.isConnected ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-400'
              }`}
            >
              <Cloud size={32} />
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h4 className="text-lg font-bold text-slate-900">Google Drive</h4>
                {status?.isConnected ? (
                  <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase">
                    Actif
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-bold uppercase">
                    Inactif
                  </span>
                )}
              </div>

              <p className="text-sm text-slate-500 max-w-md">
                Archivage automatique des documents dans un espace partagé sécurisé.
              </p>

              {status?.isConnected && status.email && (
                <div className="pt-2 flex items-center gap-2 text-xs font-semibold text-[#00758d]">
                  <Database size={12} />
                  {status.email}
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0">
            {status?.isConnected ? (
              <Button
                variant="outline"
                onClick={handleDisconnect}
                disabled={isActionLoading}
                className="h-10 px-5 rounded-lg border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold text-xs transition-colors"
              >
                {isActionLoading && <RefreshCw className="w-3 h-3 mr-2" />}
                Déconnecter
              </Button>
            ) : (
              <Button
                onClick={handleConnect}
                disabled={isActionLoading}
                className="h-10 px-6 rounded-lg bg-[#00758d] hover:bg-[#00303c] text-white font-semibold text-xs transition-colors flex items-center gap-2"
              >
                {isActionLoading ? <Loader2 className="w-4 h-4" /> : <ExternalLink size={14} />}
                Lier mon compte Drive
              </Button>
            )}
          </div>
        </div>

        {/* Note info épurée */}
        <div className="mt-8 pt-8 border-t border-slate-100 flex items-start gap-4">
          <div className="w-8 h-8 rounded bg-slate-50 flex items-center justify-center text-slate-400 flex-shrink-0">
            <RefreshCw size={16} />
          </div>
          <p className="text-xs text-slate-400 leading-relaxed italic">
            Note : Une fois activé, SING créera un dossier "SingSpace_Invoices" pour synchroniser vos factures payées.
          </p>
        </div>
      </div>
    </div>
  );
}
